/* ============================================================
   Korrect flagship · motion layer
   GSAP + ScrollTrigger only. Every animation here is doing a job:
   revealing hierarchy, telling the ketone story in sequence, or
   giving feedback on a control. Nothing loops for decoration.
   ============================================================ */
(function () {
  'use strict';

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mobile = window.matchMedia('(max-width: 900px)').matches;
  const $  = (s, c) => (c || document).querySelector(s);
  const $$ = (s, c) => Array.prototype.slice.call((c || document).querySelectorAll(s));

  if (window.gsap && window.ScrollTrigger) gsap.registerPlugin(ScrollTrigger);
  /* iOS Safari fires resize every time its toolbar collapses; each one used to
     force a full ScrollTrigger refresh mid-scroll, which is the stutter you feel. */
  if (window.ScrollTrigger) ScrollTrigger.config({ ignoreMobileResize: true });
  const G = window.gsap;

  /* A page loaded in a background tab gets no animation frames, so an entrance
     tween would leave its content parked off screen. Anything that hides content
     until it animates runs through here: play it when the tab is visible, and
     otherwise jump straight to the finished state so the page is simply there. */
  function whenVisible(build) {
    let played = false;
    const play = function () {
      if (played) return; played = true;
      const tl = build();
      if (tl) setTimeout(function () { if (tl.progress() < 1) tl.progress(1); }, 6000);
    };
    const skip = function () {
      if (played) return; played = true;
      const tl = build();
      if (tl) tl.progress(1);
    };
    if (document.visibilityState === 'visible') { play(); return; }
    skip();
  }

  /* ---------------------------------------------------------
     Play videos only while they are on screen. Saves battery and
     keeps a page full of film from thrashing the decoder.
     --------------------------------------------------------- */
  const vids = $$('video[data-lazyvid], .hero__film, .close__film');
  const vio = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      const v = e.target;
      if (e.isIntersecting) { v.play().catch(function () {}); }
      else if (!v.paused) { v.pause(); }
    });
  }, { rootMargin: '200px 0px', threshold: 0.01 });
  vids.forEach(function (v) { vio.observe(v); });

  /* ---------------------------------------------------------
     1 · Load curtain. The wordmark is knocked out of the hero film
     with a multiply blend, then pulls back to open the page.
     --------------------------------------------------------- */
  const curtain = $('#curtain');
  function openCurtain() {
    if (!curtain) return;
    const done = function () { curtain.classList.add('is-done'); };
    if (reduce || !G || document.visibilityState !== 'visible') { done(); return; }
    /* belt and braces: the curtain covers the page, so it opens on a timer too */
    setTimeout(done, 4000);
    const tl = G.timeline({ onComplete: done, delay: 0.15 });
    tl.fromTo(curtain.firstElementChild, { scale: 0.94, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.5, ease: 'power2.out' })
      .to(curtain.firstElementChild, { scale: 7.2, duration: 1.25, ease: 'power3.inOut' }, '+=0.25')
      .to(curtain, { opacity: 0, duration: 0.7, ease: 'power2.inOut' }, '-=0.75');
    return tl;
  }

  /* ---------------------------------------------------------
     2 · Hero. Lines rise out of their own overflow mask so the
     headline reads as one physical movement, not a fade.
     --------------------------------------------------------- */
  function heroIn() {
    if (!G) return;
    const lines = $$('.hero__h .line > span');
    if (!reduce) {
      whenVisible(function () {
        const tl = G.timeline({ delay: 1.15 });
        tl.from(lines, { yPercent: 116, duration: 1.15, ease: 'power4.out', stagger: 0.085 })
          .from('.hero__sub', { y: 22, opacity: 0, duration: 0.8, ease: 'power3.out' }, '-=0.62')
          .from('.hero__cta > *', { y: 18, opacity: 0, duration: 0.7, ease: 'power3.out', stagger: 0.08 }, '-=0.5');
        return tl;
      });
    }

    ScrollTrigger.create({
      trigger: '.hero', start: 'top top', end: 'bottom top', scrub: true,
      animation: G.to('.hero__film', { yPercent: 12, scale: 1.14, ease: 'none' })
    });
    ScrollTrigger.create({
      trigger: '.hero', start: 'top top', end: 'bottom 40%', scrub: true,
      animation: G.to('.hero__inner', { y: -70, opacity: 0.25, ease: 'none' })
    });
  }

  /* ---------------------------------------------------------
     3 · Chrome: nav state + scroll progress.
     --------------------------------------------------------- */
  function chrome() {
    const nav = $('#nav'), bar = $('.progress i');
    ScrollTrigger.create({
      start: 'top -40', end: 99999,
      onUpdate: function (self) {
        if (nav) nav.classList.toggle('is-stuck', self.scroll() > 40);
        if (bar) bar.style.width = (self.progress * 100).toFixed(2) + '%';
      }
    });
    $$('a[href^="#"]').forEach(function (a) {
      a.addEventListener('click', function (e) {
        const t = $(a.getAttribute('href'));
        if (!t) return;
        e.preventDefault();
        window.scrollTo({ top: t.getBoundingClientRect().top + window.pageYOffset - 40, behavior: reduce ? 'auto' : 'smooth' });
      });
    });
  }

  /* ---------------------------------------------------------
     4 · Proof numbers count up once, on entry. The movement is the
     point: it says these are counted things, not decoration.
     --------------------------------------------------------- */
  function counters() {
    $$('.proof__n[data-count]').forEach(function (el) {
      const end = parseFloat(el.dataset.count);
      const dec = parseInt(el.dataset.dec || '0', 10);
      const suf = el.dataset.suffix || '';
      const comma = el.dataset.comma === '1';
      const fmt = function (v) {
        let s = v.toFixed(dec);
        if (comma) s = Number(s).toLocaleString('en-US');
        return s + suf;
      };
      if (reduce) { el.textContent = fmt(end); return; }
      const o = { v: 0 };
      ScrollTrigger.create({
        trigger: el, start: 'top 88%', once: true,
        onEnter: function () {
          G.to(o, { v: end, duration: 1.5, ease: 'power2.out', onUpdate: function () { el.textContent = fmt(o.v); } });
        }
      });
    });
  }

  /* ---------------------------------------------------------
     5 · Ketone engine. The canvas is pinned and the shader state is
     scrubbed, so the three copy beats and the visual are the same
     movement rather than two things happening near each other.
     --------------------------------------------------------- */
  function engine() {
    const canvas = $('#gl');
    if (!canvas || !window.KorrectEngine) return;
    const eng = window.KorrectEngine(canvas);
    if (!eng) { canvas.style.display = 'none'; return; }

    const beats = $$('.engine__copy .beat');
    const rails = $$('.engine__rail i');
    const setBeat = function (i) {
      beats.forEach(function (b, n) {
        G.to(b, { opacity: n === i ? 1 : 0, y: n === i ? 0 : (n < i ? -26 : 26), duration: 0.55, ease: 'power3.out', overwrite: 'auto' });
      });
      rails.forEach(function (r, n) { r.classList.toggle('on', n === i); });
    };

    if (reduce) { eng.setProgress(0.62); eng.renderOnce(); beats.forEach(function (b) { G.set(b, { opacity: 1 }); }); return; }

    G.set(beats[0], { opacity: 1 });
    rails[0] && rails[0].classList.add('on');
    /* paint one frame straight away so the section is never an empty black box,
       even before it is pinned or on a tab that is not getting frames yet */
    setTimeout(function () { eng.renderOnce(); }, 60);

    if (mobile) {
      /* Phones: kf.css holds the stage with position:sticky, so the browser does
         the pinning natively. Same field, same three beats fading in turn. */
      ScrollTrigger.create({
        trigger: '.engine', start: 'top top', end: 'bottom bottom', scrub: true,
        onEnter: function () { eng.start(); }, onEnterBack: function () { eng.start(); },
        onLeave: function () { eng.stop(); }, onLeaveBack: function () { eng.stop(); },
        onUpdate: function (self) {
          eng.setProgress(self.progress);
          setBeat(self.progress < 0.34 ? 0 : self.progress < 0.68 ? 1 : 2);
        }
      });
      return;
    }

    ScrollTrigger.create({
      trigger: '.engine', start: 'top top', end: '+=165%', pin: '.engine__stage', pinSpacing: true, scrub: true,
      onEnter: function () { eng.start(); }, onEnterBack: function () { eng.start(); },
      onLeave: function () { eng.stop(); }, onLeaveBack: function () { eng.stop(); },
      onUpdate: function (self) {
        eng.setProgress(self.progress);
        setBeat(self.progress < 0.34 ? 0 : self.progress < 0.68 ? 1 : 2);
      }
    });

    if (!mobile) {
      canvas.addEventListener('pointermove', function (e) {
        const r = canvas.getBoundingClientRect();
        eng.setPointer((e.clientX - r.left - r.width / 2) / r.height, (r.height / 2 - (e.clientY - r.top)) / r.height);
      }, { passive: true });
      canvas.addEventListener('pointerleave', function () { eng.setPointer(2, 2); }, { passive: true });
    }
  }

  /* ---------------------------------------------------------
     6 · Marquee. One on the page. It carries the brand line and it
     leans into whichever direction the reader is scrolling.
     --------------------------------------------------------- */
  function marquee() {
    const track = $('.mq__track');
    /* phones run the marquee as a CSS animation (kf.css), nothing per frame here */
    if (!track || reduce || mobile) return;
    const half = track.scrollWidth / 2;
    const tween = G.to(track, { x: -half, duration: 26, ease: 'none', repeat: -1,
      modifiers: { x: function (x) { return (parseFloat(x) % half) + 'px'; } } });
    ScrollTrigger.create({
      trigger: '.mq', start: 'top bottom', end: 'bottom top',
      onUpdate: function (self) { tween.timeScale(1 + Math.min(Math.abs(self.getVelocity()) / 1400, 3)); }
    });
  }

  /* ---------------------------------------------------------
     7 · Lineup. Vertical scroll becomes a horizontal pan across the
     three products, so the range reads as one shelf.
     --------------------------------------------------------- */
  function lineup() {
    const track = $('#lineTrack');
    if (!track || reduce || mobile) return;
    const distance = function () { return track.scrollWidth - window.innerWidth; };
    const pan = G.to(track, {
      x: function () { return -distance(); }, ease: 'none',
      scrollTrigger: {
        trigger: '.lineup', start: 'top top', end: function () { return '+=' + distance(); },
        /* No snap. Panes are a full viewport wide so nothing bleeds in anyway,
           and snap was the only thing on the page that moved the scroll on its
           own - which is what reads as "not smooth". Scroll is now 1:1. */
        pin: true, scrub: true, invalidateOnRefresh: true, anticipatePin: 1
      }
    });
    /* each packshot drifts against its own pane as that pane crosses the screen,
       which gives the shelf depth instead of three flat slides */
    $$('.pane__media img').forEach(function (img) {
      G.fromTo(img, { yPercent: 7, scale: 0.96 }, {
        yPercent: -7, scale: 1.04, ease: 'none',
        scrollTrigger: { trigger: img.closest('.pane'), containerAnimation: pan,
                         start: 'left right', end: 'right left', scrub: true }
      });
    });
  }

  /* ---------------------------------------------------------
     8 · Edge chapter. The film is scrubbed by the scrollbar, so the
     product turns at the reader's pace instead of on a loop.
     --------------------------------------------------------- */
  function edge() {
    const v = $('#edgeFilm');
    if (!v) return;
    const words = $$('.edge__h span');
    if (reduce) { v.setAttribute('loop', ''); v.play().catch(function () {}); return; }

    G.from(words, {
      yPercent: 108, opacity: 0, duration: 0.9, ease: 'power4.out', stagger: 0.055,
      scrollTrigger: { trigger: '.edge', start: 'top 45%', once: true }
    });
    G.from('.edge__p', {
      y: 24, opacity: 0, duration: 0.8, ease: 'power3.out',
      scrollTrigger: { trigger: '.edge', start: 'top 32%', once: true }
    });

    if (mobile) { v.setAttribute('loop', ''); v.play().catch(function () {}); return; }

    let dur = 0, target = 0, seeking = false;
    const onMeta = function () { dur = v.duration || 0; };
    if (v.readyState >= 1) onMeta(); else v.addEventListener('loadedmetadata', onMeta, { once: true });

    ScrollTrigger.create({
      trigger: '.edge', start: 'top top', end: 'bottom bottom', scrub: true,
      onUpdate: function (self) {
        if (!dur) return;
        target = Math.min(dur - 0.05, Math.max(0, self.progress * dur));
        if (!seeking) {
          seeking = true;
          requestAnimationFrame(function () { try { v.currentTime = target; } catch (e) {} seeking = false; });
        }
      }
    });
  }

  /* ---------------------------------------------------------
     9 · Section reveals. One shared entrance so the page has a
     consistent rhythm instead of a different idea per section.
     --------------------------------------------------------- */
  function reveals() {
    if (reduce) return;
    const groups = [
      ['.quote blockquote > *', '.quote'],
      ['.bento__h', '.bento'],
      ['.bento .cell', '.bento__grid'],
      ['.close__inner > *', '.close'],
      ['.proof__item', '.proof']
    ];
    groups.forEach(function (g) {
      const items = $$(g[0]);
      if (!items.length) return;
      G.from(items, {
        y: 34, opacity: 0, duration: 0.9, ease: 'power3.out', stagger: 0.07,
        scrollTrigger: { trigger: g[1], start: 'top 78%', once: true }
      });
    });
    G.fromTo('.quote__media img', { scale: 1.12 }, {
      scale: 1, ease: 'none',
      scrollTrigger: { trigger: '.quote', start: 'top bottom', end: 'bottom top', scrub: true }
    });
  }

  /* ---------------------------------------------------------
     10 · Magnetic CTAs. Feedback only: the control leans toward the
     pointer so it reads as touchable, and snaps back on exit.
     --------------------------------------------------------- */
  function magnets() {
    if (reduce || mobile || !window.matchMedia('(hover: hover)').matches) return;
    $$('.magnetic').forEach(function (el) {
      const x = G.quickTo(el, 'x', { duration: 0.45, ease: 'power3.out' });
      const y = G.quickTo(el, 'y', { duration: 0.45, ease: 'power3.out' });
      el.addEventListener('pointermove', function (e) {
        const r = el.getBoundingClientRect();
        x((e.clientX - r.left - r.width / 2) * 0.28);
        y((e.clientY - r.top - r.height / 2) * 0.42);
      }, { passive: true });
      el.addEventListener('pointerleave', function () { x(0); y(0); }, { passive: true });
    });
  }

  /* ---------------------------------------------------------
     11 · The hero fills whatever the page chrome above it leaves.
     Measured rather than assumed, because in the store the header and
     announcement bar can be turned on and off.
     --------------------------------------------------------- */
  function fitHero() {
    const hero = $('.hero');
    if (!hero) return;
    const set = function () {
      const top = Math.round(hero.getBoundingClientRect().top + window.pageYOffset);
      document.documentElement.style.setProperty('--kf-top', top + 'px');
      if (window.ScrollTrigger) ScrollTrigger.refresh();
    };
    set();
    let lastW = window.innerWidth;
    window.addEventListener('resize', function () {
      /* on phones the height changes with the browser toolbar; only a real width
         change (rotation) is worth a re-measure and refresh */
      if (mobile && window.innerWidth === lastW) return;
      lastW = window.innerWidth; set();
    });
    window.addEventListener('load', set);
  }

  /* --------------------------------------------------------- */
  function boot() {
    if (!G) { document.documentElement.classList.add('no-gsap'); if (curtain) curtain.classList.add('is-done'); return; }
    if (!CSS.supports('mix-blend-mode', 'multiply') && curtain) curtain.style.background = 'transparent';
    fitHero();
    openCurtain();
    heroIn();
    chrome();
    counters();
    engine();
    marquee();
    lineup();
    edge();
    reveals();
    magnets();
    window.addEventListener('load', function () { ScrollTrigger.refresh(); });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
